<script setup lang="ts">
import { computed, ref } from 'vue';
import { Table, Button } from 'ant-design-vue';
import { array, func, object } from 'vue-types';

import FileAiPromptUpdateModal from '../Form/FileAiPromptUpdateModal.vue';
import useFileAiPromptsColumns from './useFileAiPromptsColumns';

import TableBase, { props } from '@/ui/Table/Table.vue';
import type { FileAiPromptFragmentFragment } from '@/graphql/generated/graphql';
import TableSizeSelect from '@/ui/Table/TableSizeSelect.vue';
import TableColumnSelect from '@/ui/Table/TableColumnSelect.vue';
import useConfig from '@/providers/config/useConfig';
import TableColumn from '@/ui/Table/templates/TableColumn.vue';
import Refetch from '@/ui/Table/templates/Refetch.vue';
import CopyIconButton from '@/ui/CopyIconButton.vue';
import ViewIconButton from '@/ui/ViewIconButton.vue';
import JsonView from '@/ui/JsonView/JsonView.vue';
import JsonParse from '@/ui/JsonView/JsonParse.vue';

const properties = defineProps({
  ...props,
  variables: object().isRequired,
  dataSource: array<FileAiPromptFragmentFragment>().isRequired,
  getLink: func<(data: FileAiPromptFragmentFragment) => string>(),
  onClick: func<(data: FileAiPromptFragmentFragment) => void>(),
  onSelect: func<(data: FileAiPromptFragmentFragment) => void>(),
  refetch: func(),
});

const {
  columnsSelected,
  columnsFilter,
  setSorter,
  columns,
} = useFileAiPromptsColumns({
  orderBy: properties.query.orderBy,
  showSelectColumn: Boolean(properties.onSelect),
});
const config = useConfig();
const tableDensity = computed(() => config.config.tableDensity);

const updateModalOpen = ref(false);
const editingRecord = ref<FileAiPromptFragmentFragment | null>(null);

const openEdit = (record: FileAiPromptFragmentFragment) => {
  editingRecord.value = record;
  updateModalOpen.value = true;
};

const onUpdateModalOpen = (open: boolean) => {
  updateModalOpen.value = open;
  if (!open) {
    editingRecord.value = null;
  }
};
</script>

<template>
  <FileAiPromptUpdateModal
    v-if="updateModalOpen && editingRecord"
    :open="updateModalOpen"
    :record="editingRecord"
    @update:open="onUpdateModalOpen"
  />
  <TableBase
    v-bind="$props"
    :set-sorter="setSorter"
    placeholder="Search by code (default) or prompts: …"
    top-pag-class="flex space-x-4 justify-end mb-4"
  >
    <template #top_pag_right>
      <div class="flex flex-wrap items-center gap-2">
        <TableColumnSelect v-model="columnsSelected" :columns-filter="columnsFilter" />
        <TableSizeSelect />
        <Refetch v-if="refetch" :refetch="refetch" />
      </div>
    </template>
    <template #default="{ handleTableChange }">
      <Table
        :data-source="dataSource"
        :columns="columns"
        :row-key="(record) => record.id"
        :pagination="false"
        :size="tableDensity"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="onSelect &&column.key === 'onSelect'">
            <Button @click="onSelect(record as FileAiPromptFragmentFragment)">
              Select
            </Button>
          </template>
          <template v-else-if="column.key === 'actions'">
            <Button type="link" @click="openEdit(record as FileAiPromptFragmentFragment)">
              Edit
            </Button>
          </template>
          <template v-else-if="column.key === 'schema'">
            <CopyIconButton :value="record.schema" size="small" />
            <ViewIconButton size="small">
              <JsonParse :json="record.schema">
                <template #default="{ json }">
                  <JsonView :data="json" />
                </template>
              </JsonParse>
            </ViewIconButton>
          </template>
          <TableColumn
            v-else
            :column="(column.key as string)"
            :record="record"
            :get-link="getLink"
            :on-click="onClick"
            :on-select="onSelect"
            :class="onClick ? 'ant-btn-link' : ''"
          />
        </template>
      </Table>
    </template>
  </TableBase>
</template>
